import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Badge from '../../components/shared/Badge/Badge';
import Selector from '../../components/shared/Selector/Selector';
import StyledCheckbox from '../../components/shared/StyledCheckbox/StyledCheckbox';
import {
  SupplierOption,
  WizardProject,
  serviceEvaluations,
} from '../../services/service-evaluations';
import { EvaluationLimits } from '../../util/evaluationContract';
import { formatScore } from '../../util/evaluationFormat';
import { TAG_CHIP_STYLE, UNEVALUATED_META } from '../../util/evaluationStatusMeta';
import {
  MenuPagingProvider,
  PagedMenuList,
  noOptionsMessage,
  useServerSearch,
} from './useServerSearch';
import { WizardSupplier } from './wizardState';
import styles from './app-evaluation-wizard.module.scss';

type Props = {
  value: WizardSupplier[];
  onChange: (next: WizardSupplier[]) => void;
  /** The chosen existing project, whose winners are offered above the search. */
  project: WizardProject | null;
};

type Option = { value: string; label: string };

const MAX_SUPPLIERS = EvaluationLimits.MaxSuppliersPerSend;

const searchSuppliers = (q: string, page: number) =>
  serviceEvaluations
    .getSupplierOptions(q, page)
    .then(({ suppliers, hasMore }) => ({ items: suppliers, hasMore }));

const SupplierBadge = ({ children }: { children: ReactNode }) => (
  <Badge
    color={TAG_CHIP_STYLE.color}
    backgroundColor={TAG_CHIP_STYLE.backgroundColor}
    className={styles.supplierBadge}
  >
    {children}
  </Badge>
);

const SuppliersStep = ({ value, onChange, project }: Props) => {
  const [t] = useTranslation('evaluations');
  const wonPurchases = (project?.areas ?? []).filter(area => area.winners.length > 0);
  const isCheckedWinner = (supplier: WizardSupplier) =>
    wonPurchases.some(
      area =>
        area.areaId === supplier.areaId &&
        area.winners.some(winner => winner.supplierId === supplier._id),
    );
  const checkedWinners = value.filter(isCheckedWinner);
  const searched = value.filter(supplier => !isCheckedWinner(supplier));
  const { results, status, input, onInputChange, loadMore, loadingMore } =
    useServerSearch(searchSuppliers);
  const noMatch = t('wizard.suppliers.noMatch', 'Inga leverantörer matchar');
  const NoOptionsMessage = useMemo(() => noOptionsMessage<Option>(noMatch), [noMatch]);
  // A supplier added between two page loads can come back on the next page
  const suppliersById = useMemo(
    () => new Map(results.map(supplier => [supplier._id, supplier])),
    [results],
  );
  const atCap = value.length >= MAX_SUPPLIERS;
  const chips: Option[] = searched.map(supplier => ({ value: supplier._id, label: supplier.name }));
  const paging = {
    loadMore,
    loadingMoreText: loadingMore
      ? t('wizard.suppliers.loadingMore', 'Hämtar fler leverantörer…')
      : null,
  };

  const menuLabel = (supplier: SupplierOption) => {
    const areas = supplier.areaNames.join(', ');
    const rated = supplier.score !== null && supplier.evaluationCount > 0;
    return (
      <span className={styles.supplierOption}>
        <span className={styles.supplierMain}>
          <span className={styles.supplierName}>
            <span className={styles.supplierNameText} title={supplier.name}>
              {supplier.name}
            </span>
            {supplier.organizationNumber && (
              <span className={styles.supplierOrgNumber}>{supplier.organizationNumber}</span>
            )}
            {supplier.isBlocked && (
              <SupplierBadge>{t('overview.supplier.blocked', 'Spärrad')}</SupplierBadge>
            )}
          </span>
          <span className={styles.supplierAreas} title={areas || undefined}>
            {areas}
          </span>
        </span>
        {rated ? (
          <span
            className={styles.supplierScore}
            title={t('response.scoreOf', '{{score}} av 5', { score: formatScore(supplier.score) })}
          >
            <SupplierBadge>
              <span className="fa fa-star" aria-hidden="true" />
              {formatScore(supplier.score)}
            </SupplierBadge>
            <span className={styles.supplierAnswers}>
              {t('supplierScore.answerCount', {
                count: supplier.evaluationCount,
                defaultValue_one: '{{count}} svar',
                defaultValue_other: '{{count}} svar',
              })}
            </span>
          </span>
        ) : (
          <span className={styles.supplierScore}>
            <SupplierBadge>{t('wizard.suppliers.unrated', 'Inte betygsatt')}</SupplierBadge>
          </span>
        )}
      </span>
    );
  };

  const toggleWinner = (areaId: string, supplierId: string, name: string, checked: boolean) =>
    onChange(
      checked
        ? [...value, { _id: supplierId, name, areaId }]
        : value.filter(supplier => supplier._id !== supplierId),
    );

  const winners = wonPurchases.length > 0 && (
    <div className={styles.fieldStack}>
      <p className={styles.sectionLabel}>{t('wizard.suppliers.winners', 'Vinnare i projektet')}</p>
      {wonPurchases.map(area => (
        <div key={area.areaId} className={styles.winnerPurchase} data-winner-purchase={area.name}>
          <p className={styles.purchaseName}>{area.name}</p>
          <ul className={styles.winners}>
            {area.winners.map(winner => {
              const picked = value.find(supplier => supplier._id === winner.supplierId);
              const checked = picked?.areaId === area.areaId;
              return (
                <li key={winner.supplierId}>
                  <label className={styles.winner}>
                    <StyledCheckbox
                      checked={checked}
                      disabled={!checked && (Boolean(picked) || atCap)}
                      onChange={(_event: unknown, next: boolean) =>
                        toggleWinner(area.areaId, winner.supplierId, winner.name, next)
                      }
                      inputProps={{ 'aria-label': winner.name }}
                    />
                    <span className={styles.winnerName} title={winner.name}>
                      {winner.name}
                    </span>
                    {!winner.evaluated && (
                      <Badge
                        color={UNEVALUATED_META.color}
                        backgroundColor={UNEVALUATED_META.backgroundColor}
                      >
                        {t(UNEVALUATED_META.labelKey, UNEVALUATED_META.fallback)}
                      </Badge>
                    )}
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );

  return (
    <div className={styles.fields}>
      {winners}
      <div className={styles.fieldStack}>
        <MenuPagingProvider value={paging}>
          <Selector<Option, true>
            isMulti
            isSearchable
            label={t('wizard.steps.suppliers', 'Leverantörer')}
            placeholder={t(
              'wizard.suppliers.placeholder',
              'Sök på namn, organisationsnummer eller kategori',
            )}
            options={Array.from(suppliersById.values())
              .filter(supplier => !checkedWinners.some(winner => winner._id === supplier._id))
              .map(supplier => ({ value: supplier._id, label: supplier.name }))}
            value={chips}
            onChange={next =>
              onChange(
                [
                  ...checkedWinners,
                  ...next.map(
                    option =>
                      searched.find(supplier => supplier._id === option.value) ?? {
                        _id: option.value,
                        name: option.label,
                      },
                  ),
                ].slice(0, MAX_SUPPLIERS),
              )
            }
            onInputChange={onInputChange}
            getSearchString={() => input}
            isOptionDisabled={option => atCap && !value.some(item => item._id === option.value)}
            formatOptionLabel={(option, { context }) => {
              const supplier = suppliersById.get(option.value);
              return context === 'menu' && supplier ? menuLabel(supplier) : option.label;
            }}
            components={{ NoOptionsMessage, MenuList: PagedMenuList }}
            {...({
              isLoading: status === 'loading',
              loadingMessage: () => t('wizard.suppliers.loading', 'Hämtar leverantörer…'),
            } as object)}
          />
        </MenuPagingProvider>
        {status === 'failed' && (
          <p className={styles.loadError}>
            {t('wizard.suppliers.loadFailed', 'Leverantörerna kunde inte hämtas.')}
          </p>
        )}
        <p className={styles.hint}>
          {t('wizard.suppliers.count', {
            count: value.length,
            max: MAX_SUPPLIERS,
            defaultValue_one: '{{count}} vald (max {{max}})',
            defaultValue_other: '{{count}} valda (max {{max}})',
          })}
          {atCap &&
            ` · ${t('wizard.suppliers.cap', 'Högst {{max}} leverantörer per utskick.', {
              max: MAX_SUPPLIERS,
            })}`}
        </p>
      </div>
    </div>
  );
};

export default SuppliersStep;
